import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useUserContext } from '../../contexts/UserContext';
import { useCostTracking } from '../../contexts/CostTrackingContext';
import { listServiceItemsByCompanyId } from '../../graphql/companyResourceQueries';
import { fetchDailyServiceReportsForUnit } from '../../graphql/reportQueries';
import type { NormalizedReportRecord } from '../../types/reportTypes';
import type { PMPartsAndProcedure } from '../../types/pmTypes';
import {
    buildUnitUsageTotals,
    resolveUnitUsageDateRange,
    UNIT_USAGE_PERIOD_OPTIONS,
    type UnitUsagePeriodPreset,
    type UnitUsageItem,
} from '../../utils/unitUsageTotals';
import { Btn, Card, DataTable, DateField, type DataTableColumn } from '../ui';
import '../../css/CostTrackingDashboard.css';
import '../../css/SingleUnitContent.css';

const formatCurrency = (value: number) =>
    value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatQuantity = (value: number) =>
    Number.isInteger(value)
        ? value.toLocaleString('en-US')
        : value.toLocaleString('en-US', { maximumFractionDigits: 2 });

/** Calendar YYYY-MM-DD → short locale date for screen readers and display. */
const formatUsageRangeDate = (isoDate: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
    if (!match) return isoDate;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export type UnitUsagePanelProps = {
    unitId: string;
    unitName: string;
    /** Defaults to the signed-in company when omitted. */
    companyId?: string | null;
    defaultPreset?: UnitUsagePeriodPreset;
    /** When true, shows a link to the Cost Tracking module (standalone Usage only). */
    showCostModuleLink?: boolean;
    /**
     * When false (embedded under Cost/Expense), render Usage Cards as siblings of the
     * cost Cards so `.ct-unit-tab` gap applies — no extra wrapper chrome.
     */
    wrapInTab?: boolean;
};

/**
 * Period-scoped Usage totals. Expects CostTrackingContext from a parent provider
 * when embedded in Cost/Expense.
 */
export const UnitUsagePanel = ({
    unitId,
    unitName,
    companyId: companyIdProp,
    defaultPreset = 'ytd',
    showCostModuleLink = false,
    wrapInTab = true,
}: UnitUsagePanelProps) => {
    const navigate = useNavigate();
    const { company } = useUserContext();
    const { getExpensesForUnit, isLoading: expensesLoading } = useCostTracking();
    const companyId = companyIdProp ?? company?.id;

    const [preset, setPreset] = useState<UnitUsagePeriodPreset>(defaultPreset);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [reports, setReports] = useState<NormalizedReportRecord[]>([]);
    const [reportsLoading, setReportsLoading] = useState(true);
    const [reportsError, setReportsError] = useState<string | null>(null);
    const [catalog, setCatalog] = useState<PMPartsAndProcedure[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(false);

    const range = useMemo(
        () =>
            resolveUnitUsageDateRange(
                preset,
                preset === 'custom' ? { start: customStart, end: customEnd } : undefined,
            ),
        [preset, customStart, customEnd],
    );

    useEffect(() => {
        let isCancelled = false;

        (async () => {
            setReportsLoading(true);
            setReportsError(null);
            if (!companyId) {
                if (!isCancelled) {
                    setReports([]);
                    setReportsLoading(false);
                }
                return;
            }
            try {
                const items = await fetchDailyServiceReportsForUnit(unitId, {
                    dateStart: range.start,
                    dateEnd: range.end,
                    companyId,
                });
                if (!isCancelled) {
                    setReports(items);
                }
            } catch (error) {
                console.error('Failed to load Daily Service reports for usage totals:', error);
                Sentry.captureException(error);
                if (!isCancelled) {
                    setReports([]);
                    setReportsError('Could not load Daily Service usage for this period.');
                }
            } finally {
                if (!isCancelled) {
                    setReportsLoading(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [unitId, companyId, range.start, range.end]);

    useEffect(() => {
        let isCancelled = false;
        if (!companyId) {
            setCatalog([]);
            setCatalogLoading(false);
            return;
        }

        (async () => {
            setCatalogLoading(true);
            try {
                const items = await listServiceItemsByCompanyId(companyId);
                if (!isCancelled) {
                    setCatalog((items || []) as unknown as PMPartsAndProcedure[]);
                }
            } catch (error) {
                console.error('Failed to load service-item catalog for usage totals:', error);
                Sentry.captureException(error);
                if (!isCancelled) {
                    setCatalog([]);
                }
            } finally {
                if (!isCancelled) {
                    setCatalogLoading(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [companyId]);

    const expenses = useMemo(() => getExpensesForUnit(unitId), [getExpensesForUnit, unitId]);

    const totals = useMemo(
        () =>
            buildUnitUsageTotals({
                unitId,
                range,
                reports,
                expenses,
                catalog,
            }),
        [unitId, range, reports, expenses, catalog],
    );

    const costTrackingOn = Boolean(company?.costTrackingModuleEnrollment);

    const columns = useMemo<DataTableColumn<UnitUsageItem>[]>(
        () => [
            {
                id: 'item',
                header: 'Item',
                mobileCardPrimary: true,
                renderCell: (row) => row.name,
            },
            {
                id: 'quantity',
                header: 'Qty used',
                align: 'right',
                renderCell: (row) => formatQuantity(row.quantity),
            },
            {
                id: 'uom',
                header: 'Unit',
                renderCell: (row) => row.unitOfMeasure?.trim() || '—',
            },
            {
                id: 'cost',
                header: 'Cost',
                align: 'right',
                renderCell: (row) => (row.cost > 0 ? formatCurrency(row.cost) : '—'),
            },
        ],
        [],
    );

    const isLoading = reportsLoading || expensesLoading || catalogLoading;
    const rangeLabel = `${formatUsageRangeDate(range.start)} to ${formatUsageRangeDate(range.end)}`;

    const body = (
        <div className="unit-usage-panel" aria-busy={isLoading}>
            <Card variant="card-sm" className="ct-unit-summary-card unit-usage-controls-card">
                <h2>Usage</h2>
                <p className="unit-usage-intro">
                    Totals what this unit used from Daily Service logs
                    {costTrackingOn ? ' and Cost Tracking records' : ''} for the selected period.
                    Daily Service line items are not double-counted when a matching cost record
                    exists.
                </p>

                <div className="unit-usage-period-chips" role="group" aria-label="Usage period">
                    {UNIT_USAGE_PERIOD_OPTIONS.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            className={`unit-usage-period-chip${preset === option.id ? ' is-active' : ''}`}
                            aria-pressed={preset === option.id}
                            onClick={() => {
                                setPreset(option.id);
                                if (option.id === 'custom' && !customStart && !customEnd) {
                                    setCustomStart(range.start);
                                    setCustomEnd(range.end);
                                }
                            }}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                {preset === 'custom' && (
                    <div className="unit-usage-custom-range">
                        <div className="unit-usage-date-field">
                            <span className="unit-usage-date-field-label">From</span>
                            <DateField
                                value={customStart}
                                onChange={setCustomStart}
                                ariaLabel="Usage period start date"
                            />
                        </div>
                        <div className="unit-usage-date-field">
                            <span className="unit-usage-date-field-label">To</span>
                            <DateField
                                value={customEnd}
                                onChange={setCustomEnd}
                                ariaLabel="Usage period end date"
                            />
                        </div>
                    </div>
                )}

                <p className="unit-usage-range-label">Showing {rangeLabel}</p>
            </Card>

            {isLoading ? (
                <div className="ct-unit-tab-loading" role="status" aria-live="polite">
                    <p>Loading usage totals…</p>
                </div>
            ) : (
                <>
                    {reportsError && (
                        <p className="unit-usage-error" role="alert">
                            {reportsError}
                        </p>
                    )}

                    <Card variant="card-sm" className="ct-unit-summary-card">
                        <h2>Period summary</h2>
                        <div className="ct-unit-summary-metrics">
                            <div className="ct-unit-metric">
                                <span className="ct-unit-metric-label">Recorded cost</span>
                                <span className="ct-unit-metric-value">
                                    {costTrackingOn ? formatCurrency(totals.costTotal) : '—'}
                                </span>
                            </div>
                            <div className="ct-unit-metric">
                                <span className="ct-unit-metric-label">Daily services</span>
                                <span className="ct-unit-metric-value">{totals.reportCount}</span>
                            </div>
                            <div className="ct-unit-metric">
                                <span className="ct-unit-metric-label">Cost records</span>
                                <span className="ct-unit-metric-value">
                                    {costTrackingOn ? totals.expenseCount : '—'}
                                </span>
                            </div>
                        </div>
                        {!costTrackingOn && (
                            <p className="ct-unit-empty-msg">
                                Cost Tracking is off for this company, so dollar totals are hidden.
                                Consumable quantities from Daily Service still appear below.
                            </p>
                        )}
                        {costTrackingOn && (
                            <p className="ct-unit-summary-note">
                                Recorded cost is the sum of expense totals in this period. It may
                                differ from the Items used total below, which can include catalog
                                price estimates for Daily Service quantities with no expense dollars.
                            </p>
                        )}
                    </Card>

                    <Card variant="card-sm" className="ct-unit-records">
                        <h2>Items used</h2>
                        <p className="ct-unit-records-note">
                            Quantities from Daily Service answers, plus manual Cost Tracking part
                            lines that are not already covered by a Daily Service log. Cost uses
                            expense part lines when present; otherwise catalog price × quantity.
                        </p>
                        <DataTable
                            ariaLabel={`Items used by ${unitName} from ${rangeLabel}`}
                            columns={columns}
                            rows={totals.items}
                            getRowKey={(row) => row.key}
                            mobileCardLayout
                            emptyState={
                                <p className="ct-unit-empty-msg">
                                    No consumables or cost line items in this period.
                                </p>
                            }
                        />
                    </Card>

                    {showCostModuleLink && costTrackingOn && (
                        <div className="ct-unit-tab-actions">
                            <Btn
                                variant="secondary"
                                buttonShape="pill"
                                buttonText="Open Cost / Expense"
                                buttonOnClick={() => navigate('/cost-tracking')}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );

    if (!wrapInTab) {
        return <>{body}</>;
    }

    return <div className="unit-usage-tab ct-unit-tab">{body}</div>;
};
